import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ExternalLink, Monitor, Smartphone, Tablet } from 'lucide-react';
import { toast } from 'sonner';
import type { HelpKey } from '@zt/help';
import {
  contrastIssues,
  FONT_KEYS,
  FONTS,
  PALETTES,
  PALETTE_KEYS,
  TEMPLATES,
  themeCss,
  withPalette,
  type Palette,
  type PaletteKey,
  type ThemeTokens,
} from '@zt/theme';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
} from '@zt/ui';
import '../theme-fonts';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { ProductPage, ThemeEditor, ThemePreviewLink } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';
import { openInNewTab } from '../lib/open-tab';

/** Sözlük sırasından bağımsız karşılaştırma (veritabanı JSON anahtarlarını yeniden sıralar). */
function stable(v: unknown): string {
  const sort = (x: unknown): unknown =>
    Array.isArray(x)
      ? x.map(sort)
      : x && typeof x === 'object'
        ? Object.fromEntries(
            Object.keys(x)
              .sort()
              .map((k) => [k, sort((x as Record<string, unknown>)[k])]),
          )
        : x;
  return JSON.stringify(sort(v));
}

const COLOR_HELP: Record<PaletteKey, HelpKey> = {
  background: 'design.color.background',
  surface: 'design.color.surface',
  text: 'design.color.text',
  mutedText: 'design.color.muted_text',
  border: 'design.color.border',
  primary: 'design.color.primary',
  secondary: 'design.color.secondary',
  accent: 'design.color.accent',
  button: 'design.color.button',
  buttonText: 'design.color.button_text',
  buttonHover: 'design.color.button_hover',
};

type Device = 'phone' | 'tablet' | 'desktop';
const DEVICE_WIDTH: Record<Device, number> = { phone: 390, tablet: 768, desktop: 1200 };

/** Tema editörü: renk, yazı tipi, şekil ve düzen; canlı önizleme; taslak → yayım → sürüm geçmişi → geri al. */
export function DesignPage() {
  const t = useT();
  const { can } = usePermissions();
  const canEdit = can('theme.edit');
  const canPublish = can('theme.publish');
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const q = useQuery({
    queryKey: ['theme', storeId],
    queryFn: () => api.get<ThemeEditor>(`/v1/admin/stores/${storeId}/theme`),
    enabled: !!storeId,
  });
  const products = useQuery({
    queryKey: ['theme-preview-products'],
    queryFn: () => api.get<ProductPage>('/v1/admin/products?limit=5&status=active'),
    enabled: can('catalog.product.read'),
  });
  const [draft, setDraft] = useState<ThemeTokens | null>(null);
  const [publishing, setPublishing] = useState(false);
  useEffect(() => {
    if (q.data) setDraft(q.data.draft as ThemeTokens);
  }, [q.data]);
  const server = q.data;
  const dirty = !!draft && !!server && stable(draft) !== stable(server.draft);
  const issues = useMemo(() => (draft ? contrastIssues(draft) : []), [draft]);
  const store = stores.find((s) => s.id === storeId);

  const save = useApiMutation(
    () =>
      api.put<ThemeEditor>(`/v1/admin/stores/${storeId}/theme/draft`, {
        tokens: draft,
        version: server?.version,
      }),
    { invalidate: [['theme', storeId]], success: t('admin.design.saved') },
  );
  /** Kaydedilmemiş değişiklik varsa önce kaydeder; sunucudaki güncel sürüm numarasını döndürür. */
  const ensureSaved = async (): Promise<number | undefined> => {
    if (!dirty) return server?.version;
    const r = await api.put<ThemeEditor>(`/v1/admin/stores/${storeId}/theme/draft`, {
      tokens: draft,
      version: server?.version,
    });
    return r.version;
  };
  const preview = useApiMutation(
    async () => {
      await ensureSaved();
      return api.post<ThemePreviewLink>(`/v1/admin/stores/${storeId}/theme/preview-link`);
    },
    {
      invalidate: [['theme', storeId]],
      onSuccess: (r) => {
        if (!openInNewTab(r.url)) toast.message(t('admin.design.previewBlocked'), { description: r.url });
      },
    },
  );
  const publish = useApiMutation(
    async (note: string) => {
      const version = await ensureSaved();
      return api.post<ThemeEditor>(`/v1/admin/stores/${storeId}/theme/publish`, {
        note: note || null,
        version,
      });
    },
    {
      invalidate: [['theme', storeId]],
      success: t('admin.design.published'),
      onSuccess: () => setPublishing(false),
    },
  );
  const discard = useApiMutation(
    () => api.post<ThemeEditor>(`/v1/admin/stores/${storeId}/theme/discard`, { version: server?.version }),
    { invalidate: [['theme', storeId]], success: t('admin.design.discarded') },
  );
  const rollback = useApiMutation(
    (toVersion: number) =>
      api.post<ThemeEditor>(`/v1/admin/stores/${storeId}/theme/rollback`, {
        toVersion,
        version: server?.version,
      }),
    { invalidate: [['theme', storeId]], success: t('admin.design.rolledBack') },
  );

  if (loading) return <Spinner label={t('common.loading')} />;
  const set = (patch: (d: ThemeTokens) => ThemeTokens) =>
    setDraft((d) => (d ? patch(structuredClone(d)) : d));
  const pending = dirty || !!server?.hasUnpublishedChanges;
  return (
    <PageFrame
      helpKey="page.design"
      wide
      actions={
        server &&
        draft && (
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <>
                <Button onClick={() => preview.mutate(undefined)} disabled={preview.isPending}>
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  {t('admin.design.preview')}
                </Button>
                <HelpTip k="design.preview" />
                <Button onClick={() => save.mutate(undefined)} disabled={!dirty || save.isPending}>
                  {t('admin.design.save')}
                </Button>
                <HelpTip k="design.save" />
              </>
            )}
            {canPublish && (
              <>
                <Button
                  variant="primary"
                  onClick={() => setPublishing(true)}
                  disabled={!pending || issues.length > 0}
                >
                  {t('admin.design.publish')}
                </Button>
                <HelpTip k="design.publish" />
              </>
            )}
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : !server || !draft ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          <StatusLine view={server} dirty={dirty} issues={issues.length} />
          {dirty && canEdit && (
            <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-2">
              {t('admin.design.unsaved')}
              <Button size="sm" variant="ghost" onClick={() => setDraft(server.draft as ThemeTokens)}>
                {t('admin.design.revertLocal')}
              </Button>
              <HelpTip k="design.revert_local" />
            </p>
          )}
          {!dirty && server.hasUnpublishedChanges && server.published && canEdit && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-2">
              <ConfirmButton
                variant="ghost"
                message={t('admin.design.discardConfirm', { version: server.published.version })}
                onConfirm={() => discard.mutateAsync(undefined)}
              >
                {t('admin.design.discard')}
              </ConfirmButton>
              <HelpTip k="design.discard" />
            </div>
          )}
          <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]">
            <fieldset disabled={!canEdit} className="min-w-0 space-y-2 disabled:opacity-90">
              <legend className="sr-only">{t('admin.design.editor')}</legend>
              <TemplatesSection draft={draft} onApply={setDraft} />
              <ColorsSection draft={draft} set={set} />
              <TypographySection draft={draft} set={set} />
              <ShapeSection draft={draft} set={set} />
              <LayoutSection draft={draft} set={set} />
            </fieldset>
            <div className="min-w-0 space-y-4 lg:sticky lg:top-4 lg:self-start">
              <PreviewPane
                tokens={draft}
                storeName={store?.name ?? ''}
                products={(products.data?.items ?? []).map((p) => ({
                  title: p.title,
                  brand: p.brandName,
                  imageFileId: p.imageFileId,
                }))}
              />
              <ContrastList issues={issues} />
            </div>
          </div>
          <VersionsSection
            view={server}
            canRollback={canPublish}
            onRollback={(v) => rollback.mutateAsync(v)}
          />
          <PublishDialog
            open={publishing}
            onOpenChange={setPublishing}
            busy={publish.isPending}
            dirty={dirty}
            onPublish={(note) => publish.mutate(note)}
          />
        </>
      )}
    </PageFrame>
  );
}

function StatusLine({ view, dirty, issues }: { view: ThemeEditor; dirty: boolean; issues: number }) {
  const t = useT();
  const { formatDate } = useI18n();
  const live = view.versions.find((v) => v.current);
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      {view.published && live ? (
        <Badge tone="moss">
          {t('admin.design.liveVersion', {
            version: view.published.version,
            date: formatDate(live.publishedAt, true),
          })}
        </Badge>
      ) : (
        <Badge tone="neutral">{t('admin.design.noLiveTheme')}</Badge>
      )}
      {(dirty || view.hasUnpublishedChanges) && <Badge tone="amber">{t('admin.design.unpublished')}</Badge>}
      {issues > 0 && <Badge tone="rose">{t('admin.design.issuesCount', { count: issues })}</Badge>}
    </div>
  );
}

// ---------------------------------------------------------------------------- Bölümler

function TemplatesSection({ draft, onApply }: { draft: ThemeTokens; onApply: (t: ThemeTokens) => void }) {
  const t = useT();
  return (
    <Section
      title={t('admin.design.templates')}
      description={t('admin.design.templatesLead')}
      className="border-t-0 pt-0"
    >
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium">{t('admin.design.templatePick')}</span>
        <HelpTip k="design.templates" />
      </div>
      <ul className="mt-2 grid grid-cols-2 gap-2">
        {TEMPLATES.map((tpl) => (
          <li key={tpl.key}>
            <button
              type="button"
              onClick={() => onApply(structuredClone(tpl.tokens))}
              className="flex w-full flex-col gap-2 rounded-sm border border-line bg-surface p-2.5 text-start text-sm hover:border-line-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed"
              aria-pressed={stable(tpl.tokens) === stable(draft)}
            >
              <Swatches palette={tpl.tokens.palette} />
              <span
                className="font-medium"
                style={{ fontFamily: FONTS[tpl.tokens.typography.headingFont].family }}
              >
                {t(`admin.design.templateNames.${tpl.key}`)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Swatches({ palette }: { palette: Palette }) {
  return (
    <span className="flex h-6 overflow-hidden rounded-[3px] border border-line" aria-hidden>
      {(['background', 'surface', 'primary', 'secondary', 'accent', 'text'] as const).map((k) => (
        <span key={k} className="flex-1" style={{ background: palette[k] }} />
      ))}
    </span>
  );
}

type Setter = (patch: (d: ThemeTokens) => ThemeTokens) => void;

function ColorsSection({ draft, set }: { draft: ThemeTokens; set: Setter }) {
  const t = useT();
  const dark = draft.colorScheme === 'auto' && !!draft.darkPalette;
  const enableDark = (on: boolean) =>
    set((d) => {
      if (!on) return { ...d, colorScheme: 'light', darkPalette: null };
      const preset = PALETTES.find((p) => stable(p.light) === stable(d.palette)) ?? PALETTES[0]!;
      return { ...d, colorScheme: 'auto', darkPalette: d.darkPalette ?? { ...preset.dark } };
    });
  return (
    <Section title={t('admin.design.colors')} description={t('admin.design.colorsLead')}>
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium">{t('admin.design.palettes')}</span>
        <HelpTip k="design.palettes" />
      </div>
      <ul className="mt-2 grid grid-cols-3 gap-2">
        {PALETTES.map((p) => (
          <li key={p.key}>
            <button
              type="button"
              onClick={() => set((d) => withPalette(d, p.key) ?? d)}
              className="flex w-full flex-col gap-1.5 rounded-sm border border-line bg-surface p-2 text-start text-[0.8125rem] hover:border-line-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed"
              aria-pressed={stable(p.light) === stable(draft.palette)}
            >
              <Swatches palette={p.light} />
              {t(`admin.design.paletteNames.${p.key}`)}
            </button>
          </li>
        ))}
      </ul>
      <h3 className="mt-5 text-sm font-semibold">{t('admin.design.lightColors')}</h3>
      <PaletteFields
        palette={draft.palette}
        onChange={(k, v) => set((d) => ({ ...d, palette: { ...d.palette, [k]: v } }))}
      />
      <div className="mt-5 flex items-center gap-3">
        <Switch
          id="design-dark"
          checked={dark}
          onCheckedChange={enableDark}
          label={t('admin.design.darkMode')}
        />
        <label htmlFor="design-dark" className="text-sm font-medium">
          {t('admin.design.darkMode')}
        </label>
        <HelpTip k="design.dark_mode" />
      </div>
      {dark && draft.darkPalette && (
        <>
          <h3 className="mt-4 text-sm font-semibold">{t('admin.design.darkColors')}</h3>
          <PaletteFields
            palette={draft.darkPalette}
            onChange={(k, v) => set((d) => ({ ...d, darkPalette: { ...d.darkPalette!, [k]: v } }))}
          />
        </>
      )}
    </Section>
  );
}

const HEX = /^#[0-9a-f]{6}$/i;

function ColorField({
  label,
  value,
  help,
  onChange,
}: {
  label: string;
  value: string;
  help: HelpKey;
  onChange: (v: string) => void;
}) {
  const t = useT();
  const id = useId();
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const invalid = !HEX.test(text);
  return (
    <div className="grid grid-cols-[2.5rem_6.5rem_minmax(0,1fr)] items-center gap-x-2 gap-y-1">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value.toLowerCase())}
        aria-label={label}
        className="h-9 w-10 cursor-pointer rounded-sm border border-line-strong bg-surface p-0.5"
      />
      <Input
        id={id}
        value={text}
        spellCheck={false}
        maxLength={7}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${id}-e` : undefined}
        className="h-9 font-mono text-sm uppercase"
        onChange={(e) => {
          const v = e.target.value.trim();
          setText(v);
          if (HEX.test(v)) onChange(v.toLowerCase());
        }}
      />
      <span className="flex min-w-0 items-center gap-1">
        <label htmlFor={id} className="truncate text-sm">
          {label}
        </label>
        <HelpTip k={help} />
      </span>
      {invalid && (
        <p id={`${id}-e`} role="alert" className="col-span-3 text-[0.8125rem] font-medium text-rose">
          {t('errors.color_format')}
        </p>
      )}
    </div>
  );
}

function PaletteFields({
  palette,
  onChange,
}: {
  palette: Palette;
  onChange: (k: PaletteKey, v: string) => void;
}) {
  const t = useT();
  return (
    <div className="mt-2 grid gap-2">
      {PALETTE_KEYS.map((k) => (
        <ColorField
          key={k}
          label={t(`admin.design.colorNames.${k}`)}
          value={palette[k]}
          help={COLOR_HELP[k]}
          onChange={(v) => onChange(k, v)}
        />
      ))}
    </div>
  );
}

function Choice<T extends string | number>({
  label,
  help,
  value,
  options,
  onChange,
  labelOf,
}: {
  label: string;
  help: HelpKey;
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
  labelOf: (v: T) => string;
}) {
  return (
    <Field label={label} help={<HelpTip k={help} />}>
      {(p) => (
        <Select
          {...p}
          value={String(value)}
          onChange={(e) => onChange(options.find((o) => String(o) === e.target.value)!)}
        >
          {options.map((o) => (
            <option key={String(o)} value={String(o)}>
              {labelOf(o)}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

function TypographySection({ draft, set }: { draft: ThemeTokens; set: Setter }) {
  const t = useT();
  const ty = draft.typography;
  const font = (k: (typeof FONT_KEYS)[number]) =>
    `${FONTS[k].name} (${t(`admin.design.fontKinds.${FONTS[k].kind}`)})`;
  const upd = (patch: Partial<ThemeTokens['typography']>) =>
    set((d) => ({ ...d, typography: { ...d.typography, ...patch } }));
  return (
    <Section title={t('admin.design.typography')} description={t('admin.design.typographyLead')}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
        <Choice
          label={t('admin.design.bodyFont')}
          help="design.font_body"
          value={ty.bodyFont}
          options={FONT_KEYS}
          onChange={(v) => upd({ bodyFont: v })}
          labelOf={font}
        />
        <Choice
          label={t('admin.design.headingFont')}
          help="design.font_heading"
          value={ty.headingFont}
          options={FONT_KEYS}
          onChange={(v) => upd({ headingFont: v })}
          labelOf={font}
        />
        <Choice
          label={t('admin.design.baseSize')}
          help="design.base_size"
          value={ty.baseSize}
          options={[15, 16, 17, 18, 19] as const}
          onChange={(v) => upd({ baseSize: v })}
          labelOf={(v) => t('admin.design.px', { value: v })}
        />
        <Choice
          label={t('admin.design.headingScale')}
          help="design.heading_scale"
          value={ty.headingScale}
          options={['compact', 'normal', 'large'] as const}
          onChange={(v) => upd({ headingScale: v })}
          labelOf={(v) => t(`admin.design.headingScales.${v}`)}
        />
        <Choice
          label={t('admin.design.headingWeight')}
          help="design.heading_weight"
          value={ty.headingWeight}
          options={[500, 600, 700] as const}
          onChange={(v) => upd({ headingWeight: v })}
          labelOf={(v) => t(`admin.design.weights.w${v}`)}
        />
      </div>
    </Section>
  );
}

function ShapeSection({ draft, set }: { draft: ThemeTokens; set: Setter }) {
  const t = useT();
  return (
    <Section title={t('admin.design.shape')} description={t('admin.design.shapeLead')}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
        <Field label={t('admin.design.radius')} help={<HelpTip k="design.radius" />}>
          {(p) => (
            <div className="flex items-center gap-3">
              <input
                {...p}
                type="range"
                min={0}
                max={24}
                step={1}
                value={draft.shape.radius}
                onChange={(e) =>
                  set((d) => ({ ...d, shape: { ...d.shape, radius: Number(e.target.value) } }))
                }
                className="w-full accent-[var(--color-pine)]"
              />
              <span className="w-12 text-end text-sm tabular">
                {t('admin.design.px', { value: draft.shape.radius })}
              </span>
            </div>
          )}
        </Field>
        <Choice
          label={t('admin.design.buttonShape')}
          help="design.button_shape"
          value={draft.shape.buttonShape}
          options={['square', 'rounded', 'pill'] as const}
          onChange={(v) => set((d) => ({ ...d, shape: { ...d.shape, buttonShape: v } }))}
          labelOf={(v) => t(`admin.design.buttonShapes.${v}`)}
        />
        <Choice
          label={t('admin.design.shadow')}
          help="design.shadow"
          value={draft.shadow}
          options={['none', 'soft', 'strong'] as const}
          onChange={(v) => set((d) => ({ ...d, shadow: v }))}
          labelOf={(v) => t(`admin.design.shadows.${v}`)}
        />
      </div>
    </Section>
  );
}

function LayoutSection({ draft, set }: { draft: ThemeTokens; set: Setter }) {
  const t = useT();
  return (
    <Section title={t('admin.design.layout')} description={t('admin.design.layoutLead')}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
        <Choice
          label={t('admin.design.cardStyle')}
          help="design.card_style"
          value={draft.card.style}
          options={['bordered', 'flat', 'raised'] as const}
          onChange={(v) => set((d) => ({ ...d, card: { ...d.card, style: v } }))}
          labelOf={(v) => t(`admin.design.cardStyles.${v}`)}
        />
        <Choice
          label={t('admin.design.imageRatio')}
          help="design.image_ratio"
          value={draft.card.imageRatio}
          options={['square', 'portrait', 'landscape'] as const}
          onChange={(v) => set((d) => ({ ...d, card: { ...d.card, imageRatio: v } }))}
          labelOf={(v) => t(`admin.design.imageRatios.${v}`)}
        />
        <Choice
          label={t('admin.design.gridDesktop')}
          help="design.grid_desktop"
          value={draft.productGrid.columnsDesktop}
          options={[2, 3, 4, 5] as const}
          onChange={(v) => set((d) => ({ ...d, productGrid: { ...d.productGrid, columnsDesktop: v } }))}
          labelOf={(v) => t('admin.design.columns', { count: v })}
        />
        <Choice
          label={t('admin.design.gridMobile')}
          help="design.grid_mobile"
          value={draft.productGrid.columnsMobile}
          options={[1, 2] as const}
          onChange={(v) => set((d) => ({ ...d, productGrid: { ...d.productGrid, columnsMobile: v } }))}
          labelOf={(v) => t('admin.design.columns', { count: v })}
        />
        <p className="text-sm text-ink-2">
          {t('admin.design.headerLayoutMoved')}{' '}
          <Link to="/sayfa-tasarimlari" className="font-medium text-pine underline">
            {t('admin.nav.pageDesigns')}
          </Link>
        </p>
        <Choice
          label={t('admin.design.headerStyle')}
          help="design.header_style"
          value={draft.header.style}
          options={['surface', 'brand'] as const}
          onChange={(v) => set((d) => ({ ...d, header: { ...d.header, style: v } }))}
          labelOf={(v) => t(`admin.design.headerStyles.${v}`)}
        />
        <div className="flex items-center gap-3">
          <Switch
            id="design-sticky"
            checked={draft.header.sticky}
            onCheckedChange={(v) => set((d) => ({ ...d, header: { ...d.header, sticky: v } }))}
            label={t('admin.design.headerSticky')}
          />
          <label htmlFor="design-sticky" className="text-sm font-medium">
            {t('admin.design.headerSticky')}
          </label>
          <HelpTip k="design.header_sticky" />
        </div>
        <Choice
          label={t('admin.design.footerStyle')}
          help="design.footer_style"
          value={draft.footer.style}
          options={['surface', 'dark', 'brand'] as const}
          onChange={(v) => set((d) => ({ ...d, footer: { style: v } }))}
          labelOf={(v) => t(`admin.design.footerStyles.${v}`)}
        />
        <Choice
          label={t('admin.design.gallery')}
          help="design.gallery"
          value={draft.productPage.galleryPosition}
          options={['start', 'end'] as const}
          onChange={(v) => set((d) => ({ ...d, productPage: { galleryPosition: v } }))}
          labelOf={(v) => t(`admin.design.galleryPositions.${v}`)}
        />
        <Choice
          label={t('admin.design.blogLayout')}
          help="design.blog_layout"
          value={draft.blog.layout}
          options={['grid', 'list'] as const}
          onChange={(v) => set((d) => ({ ...d, blog: { layout: v } }))}
          labelOf={(v) => t(`admin.design.blogLayouts.${v}`)}
        />
        <Choice
          label={t('admin.design.inputStyle')}
          help="design.input_style"
          value={draft.form.inputStyle}
          options={['outline', 'filled'] as const}
          onChange={(v) => set((d) => ({ ...d, form: { inputStyle: v } }))}
          labelOf={(v) => t(`admin.design.inputStyles.${v}`)}
        />
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------- Önizleme

interface PreviewProduct {
  title: string;
  brand: string | null;
  imageFileId: string | null;
}

function PreviewPane({
  tokens,
  storeName,
  products,
}: {
  tokens: ThemeTokens;
  storeName: string;
  products: PreviewProduct[];
}) {
  const t = useT();
  const [device, setDevice] = useState<Device>('desktop');
  const [scheme, setScheme] = useState<'light' | 'dark'>('light');
  const hasDark = tokens.colorScheme === 'auto' && !!tokens.darkPalette;
  const active = hasDark ? scheme : 'light';
  const devices: Array<{ key: Device; icon: ReactNode }> = [
    { key: 'phone', icon: <Smartphone className="h-4 w-4" aria-hidden /> },
    { key: 'tablet', icon: <Tablet className="h-4 w-4" aria-hidden /> },
    { key: 'desktop', icon: <Monitor className="h-4 w-4" aria-hidden /> },
  ];
  return (
    <section aria-labelledby="design-preview-h" className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <h2 id="design-preview-h" className="font-cond text-lg font-semibold">
            {t('admin.design.livePreview')}
          </h2>
          <HelpTip k="design.live_preview" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label={t('admin.design.device')}
            className="flex rounded-sm border border-line"
          >
            {devices.map((d) => (
              <button
                key={d.key}
                type="button"
                aria-pressed={device === d.key}
                onClick={() => setDevice(d.key)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-ink-2 aria-pressed:bg-pine-tint aria-pressed:text-pine"
              >
                {d.icon}
                {t(`admin.design.devices.${d.key}`)}
              </button>
            ))}
          </div>
          <HelpTip k="design.device" />
          {hasDark && (
            <div
              role="group"
              aria-label={t('admin.design.scheme')}
              className="flex rounded-sm border border-line"
            >
              {(['light', 'dark'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={active === s}
                  onClick={() => setScheme(s)}
                  className="px-2.5 py-1.5 text-sm text-ink-2 aria-pressed:bg-pine-tint aria-pressed:text-pine"
                >
                  {t(`admin.design.schemes.${s}`)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <ScaledFrame width={DEVICE_WIDTH[device]}>
        <MockStore
          tokens={tokens}
          scheme={active}
          device={device}
          storeName={storeName}
          products={products}
        />
      </ScaledFrame>
    </section>
  );
}

/** Seçilen cihaz genişliğindeki önizlemeyi kutuya sığacak biçimde küçültür (yazı boyutları korunur). */
function ScaledFrame({ width, children }: { width: number; children: ReactNode }) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ scale: 1, height: 0 });
  useLayoutEffect(() => {
    const measure = () => {
      const avail = outer.current?.clientWidth ?? width;
      const scale = Math.min(1, avail / width);
      setBox({ scale, height: (inner.current?.scrollHeight ?? 0) * scale });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (outer.current) ro.observe(outer.current);
    if (inner.current) ro.observe(inner.current);
    return () => ro.disconnect();
  }, [width]);
  return (
    <div
      ref={outer}
      className="overflow-hidden rounded-md border border-line bg-surface-2"
      style={{ height: box.height || undefined }}
    >
      <div
        ref={inner}
        className="origin-top-left rtl:origin-top-right"
        style={{ width, transform: `scale(${box.scale})` }}
        aria-hidden
        inert
      >
        {children}
      </div>
    </div>
  );
}

function MockStore({
  tokens,
  scheme,
  device,
  storeName,
  products,
}: {
  tokens: ThemeTokens;
  scheme: 'light' | 'dark';
  device: Device;
  storeName: string;
  products: PreviewProduct[];
}) {
  const t = useT();
  const css = themeCss(tokens);
  const pre = scheme === 'dark' ? '--thd-' : '--thl-';
  const vars: Record<string, string> = {};
  for (const [k, v] of Object.entries(css.style)) {
    if (k.startsWith('--th-')) vars[k] = v;
    else if (k.startsWith(pre)) vars[`--p-${k.slice(pre.length)}`] = v;
  }
  const cols =
    device === 'phone'
      ? tokens.productGrid.columnsMobile
      : device === 'tablet'
        ? Math.min(3, tokens.productGrid.columnsDesktop)
        : tokens.productGrid.columnsDesktop;
  // Kataloğunuzdaki ürünler; sütunları doldurmaya yetmezse boş yer tutucu kartlar (yalnızca "Ürün adı").
  const items: PreviewProduct[] = [
    ...products.slice(0, cols),
    ...Array.from({ length: Math.max(0, cols - products.length) }, () => ({
      title: t('admin.design.mock.product'),
      brand: null,
      imageFileId: null,
    })),
  ];
  return (
    <div className="tp" style={vars as CSSProperties} {...css.attrs} data-device={device}>
      <header className="tp-header">
        <span className="tp-brand">
          <span className="tp-logo" />
          {storeName || t('admin.design.mock.store')}
        </span>
        <span className="tp-search">{t('admin.design.mock.search')}</span>
        <span className="tp-links">
          <span>{t('admin.design.mock.account')}</span>
          <span>{t('admin.design.mock.cart')}</span>
        </span>
      </header>
      <main className="tp-main">
        <h1 className="tp-h1">{t('admin.design.mock.heading')}</h1>
        <p className="tp-muted">{t('admin.design.mock.lead')}</p>
        <p>
          {t('admin.design.mock.body')} <a href="#onizleme">{t('admin.design.mock.link')}</a>
        </p>
        <div className="tp-row">
          <span className="tp-btn tp-btn-primary">{t('admin.design.mock.primaryButton')}</span>
          <span className="tp-btn">{t('admin.design.mock.secondaryButton')}</span>
          <span className="tp-tag tp-tag-secondary">{t('admin.design.mock.secondaryTag')}</span>
          <span className="tp-tag tp-tag-accent">{t('admin.design.mock.accentTag')}</span>
        </div>
        <h2 className="tp-h2">{t('admin.design.mock.products')}</h2>
        <div className="tp-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {items.map((p, i) => (
            <div key={i} className="tp-card">
              <div className="tp-img">
                {p.imageFileId && <img src={`/v1/files/${p.imageFileId}/content`} alt="" />}
              </div>
              <div className="tp-cardbody">
                {p.brand && <span className="tp-small tp-muted">{p.brand}</span>}
                <span className="tp-cardtitle">{p.title}</span>
                <span className="tp-price">{t('admin.design.mock.price')}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="tp-form">
          <span className="tp-label">{t('admin.design.mock.field')}</span>
          <span className="tp-input">{t('admin.design.mock.placeholder')}</span>
          <span className="tp-small tp-muted">{t('admin.design.mock.fieldHelp')}</span>
        </div>
      </main>
      <footer className="tp-footer">
        <span>{storeName || t('admin.design.mock.store')}</span>
        <span>{t('admin.design.mock.footer')}</span>
      </footer>
    </div>
  );
}

function ContrastList({ issues }: { issues: ReturnType<typeof contrastIssues> }) {
  const t = useT();
  const { locale } = useI18n();
  const num = (v: number, digits: number) =>
    v.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  if (!issues.length)
    return (
      <p className="flex items-center gap-1.5 text-sm text-moss">
        {t('admin.design.contrastOk')}
        <HelpTip k="design.contrast" />
      </p>
    );
  return (
    <div className="rounded-md border border-rose/30 bg-rose-tint p-3 text-sm" role="status">
      <p className="flex items-center gap-1.5 font-medium text-ink">
        {t('admin.design.contrastTitle')}
        <HelpTip k="design.contrast" />
      </p>
      <ul className="mt-1.5 list-disc space-y-0.5 ps-5 text-ink">
        {issues.map((i) => (
          <li key={`${i.palette}-${i.code}`}>
            {t('admin.design.contrastIssue', {
              palette: t(`admin.design.schemes.${i.palette}`),
              fg: t(`admin.design.colorNames.${i.fg}`),
              bg: t(`admin.design.colorNames.${i.bg}`),
              ratio: num(i.ratio, 2),
              min: num(i.min, 1),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

function VersionsSection({
  view,
  canRollback,
  onRollback,
}: {
  view: ThemeEditor;
  canRollback: boolean;
  onRollback: (version: number) => Promise<unknown>;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  return (
    <Section
      title={t('admin.design.versions')}
      description={t('admin.design.versionsLead')}
      className="mt-10"
      action={<HelpTip k="design.versions" />}
    >
      {!view.versions.length ? (
        <p className="text-sm text-ink-2">{t('admin.design.noVersions')}</p>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {view.versions.map((v) => (
            <li key={v.version} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm">
              <span className="font-medium tabular">
                {t('admin.design.versionNo', { version: v.version })}
              </span>
              <span className="text-ink-2">
                {formatDate(v.publishedAt, true)}
                {v.publishedBy ? ` · ${v.publishedBy}` : ''}
              </span>
              {v.source === 'rollback' && (
                <Badge tone="slate">
                  {t('admin.design.restoredFrom', { version: v.restoredFrom ?? '' })}
                </Badge>
              )}
              {v.current && <Badge tone="moss">{t('admin.design.current')}</Badge>}
              {v.note && <span className="min-w-0 flex-1 text-ink-2">{v.note}</span>}
              {canRollback && !v.current && (
                <span className="ms-auto flex items-center gap-1.5">
                  <ConfirmButton
                    variant="secondary"
                    message={t('admin.design.rollbackConfirm', { version: v.version })}
                    onConfirm={() => onRollback(v.version)}
                  >
                    {t('admin.design.rollback')}
                  </ConfirmButton>
                  <HelpTip k="design.rollback" />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function PublishDialog({
  open,
  onOpenChange,
  busy,
  dirty,
  onPublish,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  busy: boolean;
  dirty: boolean;
  onPublish: (note: string) => void;
}) {
  const t = useT();
  const [note, setNote] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('admin.design.publishTitle')}
      description={t('admin.design.publishLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={busy} onClick={() => onPublish(note.trim())}>
            {t('admin.design.publishConfirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {dirty && <p className="text-sm text-ink-2">{t('admin.design.publishSavesFirst')}</p>}
        <Field
          label={t('admin.design.note')}
          help={<HelpTip k="design.note" />}
          optionalLabel={t('common.optional')}
        >
          {(p) => <Textarea {...p} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}
